import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  driftEventsApi,
  servicesApi,
  groupsApi,
  DriftEvent,
} from "../api/client";
import EnvironmentSelect from "@/components/EnvironmentSelect";
import { titleCase } from "../lib/utils";

export default function DriftEvents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<
    "unresolved" | "resolved" | "all"
  >("unresolved");
  const [environmentFilter, setEnvironmentFilter] = useState<string>("");
  const [serviceFilter, setServiceFilter] = useState<string>("");
  const [groupFilter, setGroupFilter] = useState<string>(
    searchParams.get("group") || "",
  );
  const [page, setPage] = useState(1);
  const [resolveModal, setResolveModal] = useState<{
    open: boolean;
    event: DriftEvent | null;
  }>({
    open: false,
    event: null,
  });
  const [resolutionNotes, setResolutionNotes] = useState("");

  // Accept modal state
  const [acceptModal, setAcceptModal] = useState<{
    open: boolean;
    event: DriftEvent | null;
  }>({
    open: false,
    event: null,
  });
  const [acceptNotes, setAcceptNotes] = useState("");
  const [acceptError, setAcceptError] = useState("");

  // View notes modal state
  const [viewNotesModal, setViewNotesModal] = useState<{
    open: boolean;
    event: DriftEvent | null;
  }>({
    open: false,
    event: null,
  });

  // Sync group filter with URL
  useEffect(() => {
    const urlGroup = searchParams.get("group") || "";
    if (urlGroup !== groupFilter) {
      setGroupFilter(urlGroup);
    }
  }, [searchParams]);

  const { data: driftEventsData, isLoading } = useQuery({
    queryKey: [
      "drift-events",
      statusFilter,
      environmentFilter,
      serviceFilter,
      groupFilter,
      page,
    ],
    queryFn: () =>
      driftEventsApi.list({
        status: statusFilter,
        environment_id: environmentFilter || undefined,
        service_id: serviceFilter || undefined,
        group: groupFilter || undefined,
        limit: 20,
        offset: (page - 1) * 20,
      }),
  });

  const { data: services } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.list(),
  });

  const { data: groupsData } = useQuery({
    queryKey: ["groups"],
    queryFn: () => groupsApi.list(),
  });

  const resolveMutation = useMutation({
    mutationFn: (params: { id: string; resolution_notes: string }) =>
      driftEventsApi.resolve(params.id, params.resolution_notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drift-events"] });
      setResolveModal({ open: false, event: null });
      setResolutionNotes("");
    },
  });

  const acceptMutation = useMutation({
    mutationFn: (params: { id: string; notes: string }) =>
      driftEventsApi.accept(params.id, params.notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drift-events"] });
      setAcceptModal({ open: false, event: null });
      setAcceptNotes("");
      setAcceptError("");
    },
    onError: (error: Error) => {
      setAcceptError(error.message || "Failed to accept drift");
    },
  });

  const driftEvents = driftEventsData?.data || [];
  const totalPages = Math.ceil((driftEventsData?.pagination?.total || 0) / 20);
  const groups = groupsData?.groups || [];

  const openResolveModal = (event: DriftEvent) => {
    setResolveModal({ open: true, event });
    setResolutionNotes("");
  };

  const handleResolve = () => {
    if (resolveModal.event) {
      resolveMutation.mutate({
        id: resolveModal.event.id,
        resolution_notes: resolutionNotes,
      });
    }
  };

  const openAcceptModal = (event: DriftEvent) => {
    setAcceptModal({ open: true, event });
    setAcceptNotes("");
    setAcceptError("");
  };

  const handleAccept = () => {
    if (!acceptNotes.trim()) {
      setAcceptError("Notes are required when accepting drift");
      return;
    }
    if (acceptModal.event) {
      acceptMutation.mutate({
        id: acceptModal.event.id,
        notes: acceptNotes,
      });
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const getTimeSince = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d ago`;
    } else if (diffHours > 0) {
      return `${diffHours}h ago`;
    } else {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins}m ago`;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Drift Events</h1>
        <div className="text-sm text-gray-500">
          {driftEventsData?.pagination?.total || 0} total events
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-wrap gap-4">
          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <div className="flex rounded-md shadow-sm">
              <button
                onClick={() => setStatusFilter("unresolved")}
                className={`px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                  statusFilter === "unresolved"
                    ? "bg-red-100 text-red-800 border-red-300"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Unresolved
              </button>
              <button
                onClick={() => setStatusFilter("resolved")}
                className={`px-3 py-1.5 text-sm font-medium border-t border-b ${
                  statusFilter === "resolved"
                    ? "bg-green-100 text-green-800 border-green-300"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Resolved
              </button>
              <button
                onClick={() => setStatusFilter("all")}
                className={`px-3 py-1.5 text-sm font-medium rounded-r-md border ${
                  statusFilter === "all"
                    ? "bg-blue-100 text-blue-800 border-blue-300"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                All
              </button>
            </div>
          </div>

          {/* Environment Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">
              Environment:
            </label>
            <EnvironmentSelect
              value={environmentFilter}
              onChange={(value) => {
                setEnvironmentFilter(value);
                setPage(1);
              }}
              className="border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          {/* Service Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">
              Service:
            </label>
            <select
              value={serviceFilter}
              onChange={(e) => {
                setServiceFilter(e.target.value);
                setPage(1);
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">All Services</option>
              {services?.services?.map((svc) => (
                <option key={svc.id} value={svc.id}>
                  {svc.name}
                </option>
              ))}
            </select>
          </div>

          {/* Group Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Group:</label>
            <select
              value={groupFilter}
              onChange={(e) => {
                const value = e.target.value;
                setGroupFilter(value);
                setPage(1);
                if (value) {
                  setSearchParams((prev) => {
                    prev.set("group", value);
                    return prev;
                  });
                } else {
                  setSearchParams((prev) => {
                    prev.delete("group");
                    return prev;
                  });
                }
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">All Groups</option>
              {groups.map((group) => (
                <option key={group.id} value={group.slug}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Drift Events Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Server / Component
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Env
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Expected
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Actual
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Detected
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap sticky right-0 bg-gray-50">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-gray-500"
                  >
                    Loading drift events...
                  </td>
                </tr>
              ) : driftEvents.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-gray-500"
                  >
                    {statusFilter === "unresolved"
                      ? "No unresolved drift events. All systems are in sync!"
                      : "No drift events found."}
                  </td>
                </tr>
              ) : (
                driftEvents.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      {event.resolved_at ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Resolved
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Unresolved
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 min-w-[200px]">
                      <div
                        className="text-sm font-medium text-gray-900 truncate max-w-[250px]"
                        title={event.server_hostname || "Unknown Server"}
                      >
                        {event.server_hostname || "Unknown Server"}
                      </div>
                      <div
                        className="text-sm text-gray-500 truncate max-w-[250px]"
                        title={`${event.component_name || "Unknown Component"}${event.service_name ? ` (${titleCase(event.service_name)})` : ""}`}
                      >
                        {event.component_name || "Unknown Component"}
                        {event.service_name && (
                          <span className="text-gray-400">
                            {" "}
                            ({titleCase(event.service_name)})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {event.environment_name || "Unknown"}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <code
                        className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded block truncate max-w-[180px]"
                        title={event.expected_version || "N/A"}
                      >
                        {event.expected_version || "N/A"}
                      </code>
                    </td>
                    <td className="px-4 py-4">
                      <code
                        className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded block truncate max-w-[180px]"
                        title={event.actual_version || "N/A"}
                      >
                        {event.actual_version || "N/A"}
                      </code>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getTimeSince(event.detected_at)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(event.detected_at)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right sticky right-0 bg-white">
                      {!event.resolved_at && (
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => openAcceptModal(event)}
                            className="text-amber-600 hover:text-amber-900 text-sm font-medium"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => openResolveModal(event)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            Resolve
                          </button>
                        </div>
                      )}
                      {event.resolved_at && event.resolution_notes && (
                        <button
                          onClick={() =>
                            setViewNotesModal({ open: true, event })
                          }
                          className="text-blue-600 hover:text-blue-800 text-sm hover:underline"
                        >
                          View notes
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{page}</span> of{" "}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Resolve Modal */}
      {resolveModal.open && resolveModal.event && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Resolve Drift Event
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="min-w-0">
                    <span className="text-gray-500">Server:</span>
                    <p className="font-medium break-all">
                      {resolveModal.event.server_hostname}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-500">Component:</span>
                    <p className="font-medium break-all">
                      {resolveModal.event.component_name}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 text-sm mt-4 pt-4 border-t border-gray-200">
                  <div className="min-w-0">
                    <span className="text-gray-500">Expected:</span>
                    <code className="text-green-700 text-xs block mt-1 break-all bg-green-50 px-2 py-1 rounded">
                      {resolveModal.event.expected_version}
                    </code>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-500">Actual:</span>
                    <code className="text-red-700 text-xs block mt-1 break-all bg-red-50 px-2 py-1 rounded">
                      {resolveModal.event.actual_version}
                    </code>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resolution Notes (optional)
                </label>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  rows={3}
                  placeholder="Describe how this drift was resolved..."
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setResolveModal({ open: false, event: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResolve}
                disabled={resolveMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {resolveMutation.isPending
                  ? "Resolving..."
                  : "Mark as Resolved"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accept Modal */}
      {acceptModal.open && acceptModal.event && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Accept Drift & Update Expected Version
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Warning Banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-amber-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">
                      This will update the expected version
                    </h3>
                    <p className="mt-1 text-sm text-amber-700">
                      The actual version will become the new expected version.
                      Use this when the detected version is correct (e.g.,
                      hotfix, emergency patch).
                    </p>
                  </div>
                </div>
              </div>

              {/* Version Change Preview */}
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="min-w-0">
                    <span className="text-gray-500">Server:</span>
                    <p className="font-medium break-all">
                      {acceptModal.event.server_hostname}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-500">Component:</span>
                    <p className="font-medium break-all">
                      {acceptModal.event.component_name}
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                  <div className="min-w-0">
                    <span className="block text-xs text-gray-500 mb-1">
                      Current Expected (will be replaced)
                    </span>
                    <code className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded line-through block break-all">
                      {acceptModal.event.expected_version}
                    </code>
                  </div>
                  <div className="flex justify-center">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <span className="block text-xs text-gray-500 mb-1">
                      New Expected
                    </span>
                    <code className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-semibold block break-all">
                      {acceptModal.event.actual_version}
                    </code>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {acceptError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-700">{acceptError}</p>
                </div>
              )}

              {/* Notes Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for accepting drift{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={acceptNotes}
                  onChange={(e) => {
                    setAcceptNotes(e.target.value);
                    if (acceptError) setAcceptError("");
                  }}
                  rows={3}
                  placeholder="Explain why this version is correct (e.g., 'Hotfix for critical bug #123 applied directly to production')"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setAcceptModal({ open: false, event: null });
                  setAcceptError("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                disabled={acceptMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-md hover:bg-amber-700 disabled:opacity-50"
              >
                {acceptMutation.isPending
                  ? "Accepting..."
                  : "Accept & Update Version"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Notes Modal */}
      {viewNotesModal.open && viewNotesModal.event && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Resolution Details
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="min-w-0">
                    <span className="text-gray-500">Server:</span>
                    <p className="font-medium break-all">
                      {viewNotesModal.event.server_hostname}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-500">Component:</span>
                    <p className="font-medium break-all">
                      {viewNotesModal.event.component_name}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 text-sm mt-4 pt-4 border-t border-gray-200">
                  <div className="min-w-0">
                    <span className="text-gray-500">Expected Version:</span>
                    <code className="text-green-700 text-xs block mt-1 break-all bg-green-50 px-2 py-1 rounded">
                      {viewNotesModal.event.expected_version}
                    </code>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-500">
                      Actual Version (at time of drift):
                    </span>
                    <code className="text-red-700 text-xs block mt-1 break-all bg-red-50 px-2 py-1 rounded">
                      {viewNotesModal.event.actual_version}
                    </code>
                  </div>
                </div>
                {viewNotesModal.event.resolved_at && (
                  <div className="mt-4 pt-4 border-t border-gray-200 text-sm">
                    <span className="text-gray-500">Resolved:</span>
                    <p className="font-medium">
                      {formatDate(viewNotesModal.event.resolved_at)}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resolution Notes
                </label>
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-700 whitespace-pre-wrap">
                  {viewNotesModal.event.resolution_notes}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setViewNotesModal({ open: false, event: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
